/**
 * The shape of a workspace backup, and the one list of what is in it.
 *
 * Backup and restore read the SAME manifest. If they each kept their own list,
 * a table added to one and forgotten in the other would not fail -- the backup
 * would simply be missing data, or the restore would silently drop it, and
 * nobody would find out until they needed the file to work.
 */

export const BACKUP_VERSION = 1;

/** How a table's rows are matched on restore, so an existing row is left alone. */
export interface TableSpec {
  table: string;
  /** Primary key columns. A row whose key is already present is skipped. */
  key: string[];
  /**
   * Columns pointing at a person. A backup can record who someone was, but it
   * cannot recreate their login -- so rows naming somebody who is no longer an
   * active member are skipped rather than failing the whole restore.
   */
  userRefs?: string[];
  /**
   * How hard a userRef has to be satisfied.
   *
   * 'active' (the default) means the person must still be a member: time may
   * not be written on behalf of somebody who has left. 'exists' means only
   * that the account is known. Membership rows need the looser test, and for
   * an obvious reason -- the row being restored IS the membership, so judging
   * it by current membership would refuse every one of them. History about a
   * person who has gone is still true, so the record tables use it too.
   */
  userGate?: 'active' | 'exists';
  /**
   * Columns that must not be written back.
   *
   * audit_log.id is generated always as identity, which Postgres refuses to be
   * told. The row is restored without it and takes a fresh one, which is why
   * that table is matched on what happened rather than on its key.
   */
  drop?: string[];
}

/**
 * Restore order, which is insert order: a row may only reference rows already
 * written. Reversing this list would be delete order, if a destructive restore
 * is ever added.
 */
export const BACKUP_TABLES: TableSpec[] = [
  { table: 'clients', key: ['id'] },
  { table: 'projects', key: ['id'] },
  { table: 'tasks', key: ['id'] },
  { table: 'tags', key: ['id'] },
  { table: 'project_fixed_prices', key: ['id'], userRefs: ['created_by'] },
  { table: 'task_fixed_prices', key: ['id'], userRefs: ['created_by'] },
  { table: 'user_hourly_rates', key: ['id'], userRefs: ['user_id', 'created_by'] },
  { table: 'time_entries', key: ['id'], userRefs: ['user_id'] },
  { table: 'time_entry_tags', key: ['entry_id', 'tag_id'] },
  { table: 'submissions', key: ['workspace_id', 'user_id', 'week_start'], userRefs: ['user_id', 'submitted_by'] },
];

/**
 * What a COMPLETE backup adds, and where each piece has to sit.
 *
 * Order is insert order, and it is not cosmetic: time_entries.import_batch_id
 * points at import_batches, so the batches have to exist before the entries
 * that name them -- which is why that one table goes in front of the standard
 * list rather than after it.
 */
export const COMPLETE_BEFORE: TableSpec[] = [
  { table: 'import_batches', key: ['id'], userRefs: ['created_by'], userGate: 'exists' },
];

export const COMPLETE_AFTER: TableSpec[] = [
  { table: 'pending_import_rows', key: ['id'] },
  {
    table: 'memberships',
    key: ['workspace_id', 'user_id'],
    userRefs: ['user_id'],
    userGate: 'exists',
  },
  {
    table: 'access_requests',
    key: ['id'],
    userRefs: ['user_id', 'decided_by'],
    userGate: 'exists',
  },
  {
    // Matched on what happened, not on id: see TableSpec.drop.
    table: 'audit_log',
    key: ['workspace_id', 'created_at', 'table_name', 'record_id', 'action'],
    userRefs: ['actor_id'],
    userGate: 'exists',
    drop: ['id'],
  },
];

export type BackupScope = 'standard' | 'complete';

/** The tables a backup of this scope covers, in the order they restore. */
export function tablesFor(scope: BackupScope): TableSpec[] {
  if (scope !== 'complete') return BACKUP_TABLES;
  return [...COMPLETE_BEFORE, ...BACKUP_TABLES, ...COMPLETE_AFTER];
}

/**
 * Captured for the record, never written back.
 *
 * profiles.id references auth.users, so a profile cannot be written for an
 * account that does not exist -- and where the account DOES exist the profile
 * already does too, created when they signed up. Restoring it could only ever
 * be a no-op or a foreign key error, so it is recorded and not attempted.
 *
 * memberships are different, and a complete backup does restore them: the
 * account they name is already there, so putting somebody back into a
 * workspace is a row this file really can replace.
 */
export const REFERENCE_TABLES = ['memberships', 'profiles'] as const;

export interface BackupFile {
  format: 'cadence.backup';
  version: number;
  workspaceId: string;
  workspaceName: string;
  takenAt: string;
  takenBy: string;
  /**
   * Which tables this file covers. Absent means a file written before scopes
   * existed, which is a standard one.
   */
  scope?: BackupScope;
  /**
   * The workspace's own row, in a complete backup.
   *
   * Only the name used to be kept, and the name is the one part of it nobody
   * would fail to notice was missing. The settings are the opposite: rounding,
   * week start, the time and duration formats, and the rules about requiring a
   * project or a client all come back as defaults after a restore without
   * them, and nothing says they have.
   */
  workspace?: { name: string; settings: Record<string, unknown> };
  /** table name -> rows, exactly as the database returned them. */
  data: Record<string, Record<string, unknown>[]>;
  /** People and memberships, for reference only. */
  reference: Record<string, Record<string, unknown>[]>;
}

export interface TableResult {
  table: string;
  /** Rows in the file. */
  found: number;
  /** Rows written. */
  restored: number;
  /** Rows already present, matched on the primary key. */
  skippedExisting: number;
  /** Rows naming somebody who is not an active member any more. */
  skippedMissingUser: number;
  /** Rows the database refused, with the first few reasons. */
  failed: number;
  errors: string[];
}

/**
 * Is this actually one of our backups?
 *
 * Checked before anything is written. A restore that got halfway through a
 * file it did not understand would be the worst outcome available, so the
 * whole file is validated up front and the first problem stops it.
 */
export function readBackup(input: unknown): { file: BackupFile } | { error: string } {
  if (typeof input !== 'object' || input === null) {
    return { error: 'That file is not a backup.' };
  }
  const f = input as Partial<BackupFile>;

  if (f.format !== 'cadence.backup') {
    return { error: 'That file is not a Cadence backup.' };
  }
  if (typeof f.version !== 'number' || !Number.isFinite(f.version)) {
    return { error: 'That backup has no version and cannot be read.' };
  }
  if (f.version > BACKUP_VERSION) {
    return {
      error:
        'That backup was written by a newer version of Cadence (format ' +
        f.version +
        ', this understands ' +
        BACKUP_VERSION +
        ').',
    };
  }
  if (typeof f.workspaceId !== 'string' || f.workspaceId.length === 0) {
    return { error: 'That backup does not say which workspace it came from.' };
  }
  if (typeof f.data !== 'object' || f.data === null) {
    return { error: 'That backup has no data in it.' };
  }

  for (const spec of tablesFor(f.scope === 'complete' ? 'complete' : 'standard')) {
    const rows = f.data[spec.table];
    if (rows === undefined) continue;
    if (!Array.isArray(rows)) {
      return { error: 'The ' + spec.table + ' section of that backup is damaged.' };
    }
  }

  return { file: f as BackupFile };
}

/** The key of one row, for matching against what is already there. */
export function rowKey(row: Record<string, unknown>, key: string[]): string {
  return key.map((k) => String(row[k] ?? '')).join('\u0000');
}

/**
 * Split a table's rows into what can be written and what cannot.
 *
 * Pure, so the rules that decide to skip a row are testable without a
 * database -- they are the part that quietly loses data if they are wrong.
 */
export function planRows(
  rows: Record<string, unknown>[],
  spec: TableSpec,
  workspaceId: string,
  existing: Set<string>,
  activeUsers: Set<string>,
  /**
   * Everyone with an account, for the specs gated on 'exists'. Defaults to the
   * active members, which keeps every existing caller and test on the stricter
   * rule they were written against.
   */
  knownUsers: Set<string> = activeUsers,
): { write: Record<string, unknown>[]; skippedExisting: number; skippedMissingUser: number } {
  const write: Record<string, unknown>[] = [];
  let skippedExisting = 0;
  let skippedMissingUser = 0;

  for (const row of rows) {
    // A backup belongs to the workspace it was taken from. Anything else in
    // the file is not ours to write.
    if (row.workspace_id !== workspaceId) continue;

    if (existing.has(rowKey(row, spec.key))) {
      skippedExisting++;
      continue;
    }

    const refs = spec.userRefs ?? [];
    const allowed = spec.userGate === 'exists' ? knownUsers : activeUsers;
    const missing = refs.some((c) => {
      const v = row[c];
      return typeof v === 'string' && v.length > 0 && !allowed.has(v);
    });
    if (missing) {
      skippedMissingUser++;
      continue;
    }

    if (spec.drop?.length) {
      const copy = { ...row };
      for (const c of spec.drop) delete copy[c];
      write.push(copy);
      continue;
    }

    write.push(row);
  }

  return { write, skippedExisting, skippedMissingUser };
}

/** `cadence-backup_Acme_2026-10-01.json` */
export function backupFilename(workspaceName: string, takenAt: string): string {
  const slug = workspaceName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  const day = takenAt.slice(0, 10);
  return 'cadence-backup_' + (slug || 'workspace') + '_' + day + '.json';
}
